export const company = {
  name: "Suha Builders",
  email: "suhabuilders@maasgrp.co.uk",
  phone: "+442046204069",
  phoneDisplay: "+44 20 4620 4069",
  whatsapp: "447440771743",
  whatsappDisplay: "+44 7440 771743",
  parent: "Maas Group of Companies Limited",
  companyNumber: "16178547",
  companiesHouse: "https://find-and-update.company-information.service.gov.uk/company/16178547",
  address: "19 Osborne Road, London E7 0PJ",
};

export const whatsappUrl = `https://wa.me/${company.whatsapp}?text=${encodeURIComponent("Hello Suha Builders, I would like to discuss a residential or commercial project.")}`;

export const imageSet = (slug) => ({
  src: `/images/${slug}-1280.webp`,
  srcSet: [480, 800, 1280, 1920].map((width) => `/images/${slug}-${width}.webp ${width}w`).join(", "),
});

export const navItems = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Projects", to: "/projects" },
  { label: "About", to: "/about" },
  { label: "UK guidance", to: "/uk-guidance" },
];

export const services = [
  {
    slug: "extensions-structural-building",
    code: "01",
    title: "Extensions & structural building",
    short: "Extend, alter and strengthen residential or commercial space through a properly sequenced build.",
    description: "From early access planning to shell, openings and making good, we coordinate the physical build around drawings, approvals and how the finished property needs to work.",
    image: "serviceBuild",
    icon: "hammer",
    tone: "blue",
    tasks: ["Rear, side and wraparound extensions", "Brickwork, blockwork and partitions", "Structural openings and steel coordination", "Plastering, ceilings and making good"],
    guidance: "Extensions can require planning permission, Building Regulations approval and Party Wall consideration through separate routes.",
  },
  {
    slug: "plumbing-heating",
    code: "02",
    title: "Plumbing & heating",
    short: "Coordinate water, heating and drainage before finishes close the work away.",
    description: "We plan first and second-fix services around kitchens, bathrooms, occupied areas and future maintenance access, using appropriately competent specialists where required.",
    image: "servicePlumbing",
    icon: "droplets",
    tone: "green",
    tasks: ["First and second-fix plumbing", "Heating and radiator upgrades", "Kitchen appliance connections", "Drainage, repairs and testing"],
    guidance: "Gas work must be completed by a Gas Safe registered engineer. Scope and certification should be agreed before appointment.",
  },
  {
    slug: "electrical-safety-systems",
    code: "03",
    title: "Electrical & safety systems",
    short: "Integrate power, lighting, alarms and data around the final room layout.",
    description: "Electrical planning works best before walls and ceilings close. We coordinate positions, loads, lighting intent and the appropriate notification or certification route.",
    image: "serviceElectrical",
    icon: "zap",
    tone: "coral",
    tasks: ["First and second-fix electrical work", "Lighting, sockets and switching", "Consumer units and circuit alterations", "Alarms, data and smart-home preparation"],
    guidance: "Certain electrical work is notifiable and should be completed or certified through an appropriate competent-person or building-control route.",
  },
  {
    slug: "painting-decorating",
    code: "04",
    title: "Painting & decorating",
    short: "Prepare surfaces properly and deliver a clean, durable final presentation.",
    description: "Good decoration starts beneath the finish. Our scope can include repairs, preparation, protection, coatings, feature finishes and a controlled final snag.",
    image: "serviceFinishes",
    icon: "paintbrush",
    tone: "coral",
    tasks: ["Surface preparation and repairs", "Interior and exterior painting", "Feature finishes and wallpaper", "Final touch-ups and snagging"],
    guidance: "Specifications should account for substrate condition, moisture, ventilation, occupancy and product drying requirements.",
  },
  {
    slug: "kitchens-bathrooms",
    code: "05",
    title: "Kitchens & bathrooms",
    short: "Bring cabinetry, services, tiling and fittings together in hard-working rooms.",
    description: "These rooms rely on close coordination between layout, waterproofing, ventilation, electrical work, plumbing, joinery and final finishes.",
    image: "whiteKitchen",
    icon: "house",
    tone: "blue",
    supplied: true,
    tasks: ["Kitchen fitting and worktops", "Bathroom and sanitaryware installation", "Tiling and waterproofing", "Lighting, ventilation and final connections"],
    guidance: "Ventilation, electrical zones, hot-water provision and waterproofing details should be resolved before installation begins.",
  },
  {
    slug: "flooring-joinery",
    code: "06",
    title: "Flooring & joinery",
    short: "Complete rooms with accurate floors, doors, storage and tailored details.",
    description: "We align final dimensions, thresholds, trims and fitted elements so the room reads as one finished space rather than a collection of separate trades.",
    image: "livingRoom",
    icon: "layers",
    tone: "green",
    supplied: true,
    tasks: ["Timber, laminate and resilient flooring", "Skirting, architraves and doors", "Bespoke storage and fitted furniture", "Ironmongery and finishing details"],
    guidance: "Moisture levels, subfloor condition, expansion allowances and fire-door requirements should be checked before installation.",
  },
  {
    slug: "roofing-exteriors",
    code: "07",
    title: "Roofing & exteriors",
    short: "Protect the building envelope through coordinated roofline and external work.",
    description: "External repairs and alterations must manage water, movement and weather exposure. We coordinate interfaces so individual repairs work together.",
    image: "serviceRoofing",
    icon: "shield",
    tone: "blue",
    tasks: ["Roof repairs and coverings", "Gutters, fascias and rainwater goods", "External render and masonry repairs", "Windows, doors and weatherproofing"],
    guidance: "Roof alterations, insulation upgrades and replacement windows can trigger Building Regulations requirements or competent-person certification.",
  },
  {
    slug: "commercial-fit-out",
    code: "08",
    title: "Commercial fit-out & handover",
    short: "Prepare offices, retail and shared spaces for a confident return to use.",
    description: "We coordinate partitions, finishes, staff areas, building services, final cleaning and handover around programme, access and occupied-building constraints.",
    image: "serviceCommercial",
    icon: "building",
    tone: "green",
    tasks: ["Office and retail refurbishment", "Partitions, ceilings and staff areas", "Landlord and end-of-tenancy works", "Final checks, cleaning and orderly handover"],
    guidance: "Commercial work can affect fire precautions, escape routes, accessibility, landlord approvals and the responsible person’s duties.",
  },
];

export const projects = [
  { id: "kitchen-before-now", category: "kitchens-bathrooms", title: "A practical kitchen, reworked for daily life", image: "kitchenBeforeAfter", meta: "Before / now study", supplied: true },
  { id: "bright-kitchen-extension", category: "kitchens-bathrooms", title: "Light, storage and a cleaner working line", image: "whiteKitchen", meta: "Kitchen refurbishment", supplied: true },
  { id: "hallway-finish", category: "flooring-joinery", title: "The quiet confidence of a finished entrance", image: "hallway", meta: "Flooring and joinery", supplied: true },
  { id: "joinery-living-room", category: "flooring-joinery", title: "Crafted storage with a room around it", image: "livingRoom", meta: "Joinery and flooring", supplied: true },
  { id: "rear-extension-reference", category: "extensions", title: "A stronger connection to the garden", image: "extension", meta: "Extension direction", reference: true },
  { id: "period-home-reference", category: "refurbishment", title: "Modern comfort, respectful proportions", image: "heritage", meta: "Period refurbishment", reference: true },
  { id: "extension-build-reference", category: "extensions", title: "A new footprint, built in the right order", image: "serviceBuild", meta: "Extension construction", reference: true },
  { id: "plumbing-reference", category: "plumbing-heating", title: "Water and heat, planned before the finish", image: "servicePlumbing", meta: "Plumbing and heating", reference: true },
  { id: "electrical-reference", category: "electrical", title: "Power, lighting and safety integrated early", image: "serviceElectrical", meta: "Electrical installation", reference: true },
  { id: "decorating-reference", category: "painting-decorating", title: "Preparation behind a cleaner final finish", image: "serviceFinishes", meta: "Painting and decorating", reference: true },
  { id: "roofing-reference", category: "roofing-exteriors", title: "Weather protection from roofline to façade", image: "serviceRoofing", meta: "Roofing and exterior", reference: true },
  { id: "commercial-reference", category: "commercial-fitout", title: "Workplaces prepared for a confident return", image: "serviceCommercial", meta: "Commercial fit-out", reference: true },
];

export const projectFilters = [
  ["all", "All work"], ["extensions", "Extensions"], ["refurbishment", "Refurbishment"],
  ["plumbing-heating", "Plumbing / heating"], ["electrical", "Electrical"],
  ["painting-decorating", "Painting / decorating"], ["kitchens-bathrooms", "Kitchens / bathrooms"],
  ["flooring-joinery", "Flooring / joinery"], ["roofing-exteriors", "Roofing / exteriors"],
  ["commercial-fitout", "Commercial fit-out"],
];

export const guidance = [
  { code: "01", title: "Planning permission", body: "Some extensions are permitted development; others need a planning application. The rules differ by property, location and previous alterations.", link: "https://www.gov.uk/planning-permission-england-wales", label: "Planning permission overview" },
  { code: "02", title: "Building Regulations", body: "Approval is separate from planning and commonly applies to structural alterations, extensions, thermal upgrades and controlled services.", link: "https://www.gov.uk/building-regulations-approval", label: "Building Regulations approval" },
  { code: "03", title: "Party Wall matters", body: "Work near a shared wall, boundary or neighbouring excavation may engage the Party Wall etc. Act 1996.", link: "https://www.gov.uk/party-walls-building-works", label: "Party Wall guidance" },
  { code: "04", title: "CDM client duties", body: "Domestic and commercial clients have health-and-safety duties under the Construction (Design and Management) Regulations 2015.", link: "https://www.hse.gov.uk/construction/cdm/2015/domestic-clients.htm", label: "HSE domestic client guidance" },
  { code: "05", title: "Competent trades", body: "Gas work must be completed by a Gas Safe registered engineer. Certain electrical work requires notification or certification.", link: "https://www.gov.uk/find-registered-tradesperson", label: "Find registered tradespeople" },
  { code: "06", title: "Workplace fire safety", body: "Commercial refurbishment can affect fire precautions, escape routes and the responsible person’s duties.", link: "https://www.gov.uk/workplace-fire-safety-your-responsibilities", label: "Workplace fire guidance" },
  { code: "07", title: "Planning Portal", body: "The Planning Portal provides project-specific starting points for common home improvement work in England and Wales.", link: "https://www.planningportal.co.uk/permission/common-projects/extensions/planning-permission", label: "Extension guidance" },
  { code: "08", title: "Clear written contracts", body: "A clear scope, price basis, programme, change process and responsibilities should be agreed before construction starts.", link: "https://www.jctltd.co.uk/category/home-owner-contracts", label: "JCT homeowner contracts" },
];

export const locations = ["Greater London", "Surrey", "Kent", "Essex", "Hertfordshire", "Nationwide by project"];
